
import { useState, useEffect } from 'react';
import { OrderWithCustomer } from '../types/orderTypes';
import { useToast } from '@/components/ui/use-toast';
import { getCurrentUserOrganizationId } from '@/lib/supabase/products/organization';
import { getOrganizationOrders } from '@/lib/supabase/orders';
import { supabase } from '@/lib/supabase/client';

interface UseOrdersLoaderProps {
  shopId?: string;
}

export const useOrdersLoader = ({ shopId }: UseOrdersLoaderProps = {}) => {
  const [orders, setOrders] = useState<OrderWithCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchOrganizationId = async () => {
      try {
        // First try to get organization ID from localStorage
        const localOrgId = localStorage.getItem('currentOrganizationId');
        if (localOrgId) {
          console.log("Using organization ID from localStorage:", localOrgId);
          setOrganizationId(localOrgId);
          return;
        }
        
        // Fall back to database function
        const orgId = await getCurrentUserOrganizationId();
        if (orgId) {
          console.log("Using organization ID from database:", orgId);
          setOrganizationId(orgId);
        } else {
          console.error("Impossible de récupérer l'ID de l'organisation");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération de l'ID de l'organisation:", error);
      }
    };

    fetchOrganizationId();
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!organizationId) {
        console.log("Pas d'ID d'organisation, impossible de charger les commandes");
        return;
      }

      try {
        setLoading(true);
        console.log(`Chargement des commandes pour l'organisation ${organizationId}${shopId ? ` et la boutique ${shopId}` : ''}`);
        const ordersData = await getOrganizationOrders(organizationId);
        
        // Récupérer les noms des clients
        const customerIds = [...new Set(ordersData.map(order => order.customer_id))];
        
        let customerMap: {[key: string]: string} = {};
        let customerShopMap: {[key: string]: string} = {};
        
        if (customerIds.length > 0) {
          const { data: customerData, error: customersError } = await supabase
            .from('customers')
            .select('id, name, shop_id')
            .in('id', customerIds)
            .eq('organization_id', organizationId); // Filter customers by organization ID
            
          if (!customersError && customerData) {
            // Créer un mapping d'ID de client vers nom de client
            customerMap = customerData.reduce((map, customer) => {
              map[customer.id] = customer.name;
              return map;
            }, {} as {[key: string]: string});
            
            // Créer un mapping d'ID de client vers shop_id
            customerShopMap = customerData.reduce((map, customer) => {
              if (customer.shop_id) {
                map[customer.id] = customer.shop_id;
              }
              return map;
            }, {} as {[key: string]: string});
          } else {
            console.error("Erreur lors de la récupération des noms des clients:", customersError);
          }
        }
        
        // Transform orders to include customer name
        let ordersWithCustomer: OrderWithCustomer[] = ordersData.map(order => ({
          ...order,
          customer_name: customerMap[order.customer_id] || `Client inconnu`
        }));
        
        // Filter by shop if a shopId is provided
        if (shopId) {
          console.log(`Filtrage des commandes pour la boutique ${shopId}`);
          
          // Filtrer les commandes par shop_id de la commande ou par shop_id du client
          ordersWithCustomer = ordersWithCustomer.filter(order => {
            // Si la commande a déjà un shop_id qui correspond, la garder
            if (order.shop_id === shopId) {
              return true;
            }
            
            // Vérifier si le client est associé à cette boutique
            const customerShopId = customerShopMap[order.customer_id];
            return customerShopId === shopId;
          });
          
          console.log(`${ordersWithCustomer.length} commandes après filtrage par boutique`);
        }
        
        console.log(`${ordersWithCustomer.length} commandes chargées pour l'organisation ${organizationId}`);
        setOrders(ordersWithCustomer);
      } catch (error) {
        console.error("Erreur lors du chargement des commandes:", error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les commandes",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [organizationId, shopId, toast]);

  return {
    orders,
    setOrders,
    loading,
    organizationId
  };
};
